import { DataPicker, Input, Radio, Textarea, Typography } from '@/ui-kit/';
import { FieldBlock } from '@/components/field-block';
import styles from './main-info-registartion-form.module.scss';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { registrationStepFirstSchema, RegistrationStepFirstType } from '@/schemes/registration-step-first-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { debounce, throttle } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { type Profile } from '@/types/profile';
import { useGetRegions } from '@/hooks/use-get-regions';
import { SearchParamsRegions } from '@/api/region/types';
import { ButtonSubmitStep } from '../button-submit-step';
import { renderCity } from '@/utils/render-city';
import WebApp from '@twa-dev/sdk';
import { format, parse } from 'date-fns';
import { Tooltip } from 'react-tooltip';
import { IconTooltip } from '@/assets/icons';
import { SelectOption } from '@/ui-kit/select';
import { TravelPlans } from '@/components/popup-block';
import { LocationProfile } from '@/components/popup-block/location-profile';
import { eventsApi } from '@/api';
import { ANY_REGION_OPTION, ANY_REGION_OPTION_LABEL } from '@/utils/regions';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { useToast } from '@/store/useToast';
import { SuccessNotificationIcon } from '@/assets/icons';
import { Trans, useTranslation } from 'react-i18next';

const DELAY_SEND_DATA = 1000;

interface Props {
    userData?: Profile | null;
    onUpdateUserData: (data: RegistrationStepFirstType) => Promise<any>;
    nextStep: () => void;
}

export const MainInfoRegistartionForm: React.FC<Props> = ({ userData, onUpdateUserData, nextStep }) => {
    const showToast = useToast((state) => state.show);
    useEffect(() => {
        callYaMetrika('registration_step_1');
        eventsApi.updateUserEvent('aboutme_screen');
    }, []);

    const initialValues = useMemo(() => {
        let firstName = '';

        if (WebApp.initDataUnsafe.user) {
            firstName = WebApp.initDataUnsafe.user.first_name;
        }

        console.log('userData?.public_name', userData?.public_name);

        if (!userData?.public_name && firstName) {
            callYaMetrika('entered_in_name_field');
        }

        return {
            public_name: userData?.public_name ?? firstName,
            about_me: userData?.about_me ?? '',
            region: userData?.region ? { label: renderCity(userData.region), value: userData.region.id } : undefined,
            regions: userData?.regions
                ? userData.regions.map((region) => ({
                      label: renderCity(region),
                      value: region.id,
                  }))
                : [ANY_REGION_OPTION],
            gender: userData?.gender ?? undefined,
            ...(
                userData?.dob ?
                    { dob: userData.dob}
                :
                    {}
            ),
            travel_plans: userData?.travel_plans ?? '',
        };
    }, [userData]);

    const [seatchParamsCities, setSeatchParamsCities] = useState<SearchParamsRegions>({ q: '' });
    const [popupEdit, setPopupEdit] = useState(false);
    const [userTravelPlans, setUserTravelPlans] = useState(userData?.travel_plans ?? '');
    const [userRegions, setUserRegion] = useState<SelectOption[]>(() => {
        return userData?.regions
            ? userData.regions.map((region) => ({
                  label: renderCity(region),
                  value: region.id,
              }))
            : [];
    });
    const [popupEditLocation, setPopupEditLocation] = useState(false);

    const { isLoading: isLoadingCities, data: dataRegions } = useGetRegions(seatchParamsCities);

    const [isLoading, setIsLoading] = useState(false);

    const methods = useForm<RegistrationStepFirstType>({
        resolver: zodResolver(registrationStepFirstSchema),
        defaultValues: initialValues,
    });
    const {
        register,
        handleSubmit,
        formState: { errors, isDirty, isValid },
        watch,
    } = methods;

    const onSubmit = async (data: RegistrationStepFirstType) => {
        try {
            setIsLoading(true);
            if (isDirty) {
                await onUpdateUserData(data);
            }
            nextStep();
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearchCityDebounce = useCallback(
        debounce((q: string) => {
            setSeatchParamsCities({ q: q.trim() });
        }, DELAY_SEND_DATA),
        [seatchParamsCities],
    );

    const handleChangeUserDataDebounce = useCallback(
        debounce(async (data: any) => {
            return await onUpdateUserData(data);
        }, DELAY_SEND_DATA),
        [onUpdateUserData],
    );

    const plansHandler = (plans: string) => {
        setPopupEdit(false);

        setUserTravelPlans(plans);
        methods.setValue('travel_plans', plans, { shouldDirty: true, shouldValidate: true });
    };

    const locationHandler = (regions: SelectOption[]) => {
        setPopupEditLocation(false);
        setUserRegion(regions);
        // @ts-ignore
        methods.setValue('regions', regions, { shouldDirty: true, shouldValidate: true });

        handleChangeUserDataDebounce({
            region_ids: (regions as SelectOption[]).map((item) => item.value),
        });
    };

    const goalNameFieldChange = useCallback(
        throttle(() => {
            callYaMetrika('entered_in_name_field');
            eventsApi.updateUserEvent('registration_name');
        }, 2000),
        [],
    );

    const goalDobFieldChange = useCallback(
        throttle(() => {
            callYaMetrika('entered_in_DOB');
            eventsApi.updateUserEvent('registration_dob');
        }, 2000),
        [],
    );

    const goalTravelPlansFieldChange = useCallback(
        throttle(() => {
            callYaMetrika('your_travel_dreams');
            eventsApi.updateUserEvent('registration_travel_plans');
        }, 2000),
        [],
    );

    const goalAboutMeFieldChange = useCallback(
        throttle(() => {
            callYaMetrika('about_me');
            eventsApi.updateUserEvent('registration_about_me');
        }, 2000),
        [],
    );
    const goalRegionsFieldChange = useCallback(
        throttle(() => {
            callYaMetrika('search_regions');
            eventsApi.updateUserEvent('registration_regions');
        }, 2000),
        [],
    );

    const publicName = watch('public_name');
    const aboutMe = watch('about_me');
    const regions = watch('regions');
    const gender = watch('gender');
    const dob = watch('dob');
    const travelPlans = watch('travel_plans');

    useEffect(() => {
        if (publicName !== initialValues.public_name) {
            goalNameFieldChange();
        }
    }, [publicName]);

    useEffect(() => {
        if (aboutMe !== initialValues.about_me) {
            goalAboutMeFieldChange();
        }
    }, [aboutMe]);

    useEffect(() => {
        if (regions && regions.length !== initialValues.regions.length) {
            goalRegionsFieldChange();
        }
    }, [regions]);

    useEffect(() => {
        if (gender !== initialValues.gender) {
            console.log('gender changed:', gender);
            eventsApi.updateUserEvent('registration_gender');
        }
    }, [gender]);

    useEffect(() => {
        if (travelPlans !== initialValues.travel_plans) {
            goalTravelPlansFieldChange();
        }
    }, [travelPlans]);

    const { t } = useTranslation();

    if (popupEditLocation) {
        return (
            <div className={styles.formContainer}>
                <LocationProfile
                    regions={userData?.regions || []}
                    onSubmit={locationHandler}
                    onClose={() => setPopupEditLocation(false)} />
            </div>
        );
    }

    if (popupEdit) {
        return (
            <div className={styles.formContainer}>
                <TravelPlans
                    textPlans={userTravelPlans}
                    isSignUp={true}
                    onSubmit={plansHandler}
                    onClose={() => {
                        setPopupEdit(false)
                    }} />
            </div>
        );
    }

    return (
        <FormProvider {...methods}>
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.formContainer}>
                    <Typography
                        variant="p"
                        fontWeight="medium"
                        className={styles.titleForm}
                        center>
                        {t('registration.main_info')}
                    </Typography>
                    <FieldBlock error={!!errors?.public_name?.message}>
                        <Input
                            placeholder={t('your_name')}
                            label={t('name')}
                            required
                            {...register('public_name', { required: true })}
                            error={errors?.public_name?.message} />
                    </FieldBlock>
                    <FieldBlock error={!!errors?.about_me?.message}>
                        <Textarea
                            label={t('about_secondary')}
                            required
                            maxLength={255}
                            {...register('about_me', { required: true })}
                            defaultValue={userData?.about_me}
                            placeholder={t('about_me_placeholder')}
                            error={errors?.about_me?.message} />
                    </FieldBlock>
                    <FieldBlock error={!!errors?.regions?.message}>
                        <div className={styles.formLabel} id="region-tooltip">
                            <Typography
                                variant="label"
                                fontWeight="medium"
                                className={styles.required}>
                                {t('regions')}
                            </Typography>
                            <span className={styles.tooltip}>
                                <IconTooltip />
                            </span>
                        </div>
                        <div className={styles.locationItems} onClick={() => setPopupEditLocation(true)}>
                            {
                                userRegions.length ?
                                    userRegions?.map((item, index) => <span key={index}>{item.label}</span>)
                                : <span>{t(ANY_REGION_OPTION_LABEL)}</span>
                            }
                        </div>
                        <Tooltip
                            anchorSelect="#region-tooltip"
                            place="top"
                            className={styles.tooltipBlock}>
                            <Trans i18nKey="registration.region_note" />
                        </Tooltip>
                    </FieldBlock>

                    <FieldBlock error={!!errors?.gender?.message}>
                        <div className={styles.formLabel}>
                            <Typography
                                variant="label"
                                fontWeight="semibold"
                                className={styles.required}
                                isError={!!errors?.gender?.message}>
                                {t('registration.your_gender')}
                            </Typography>
                        </div>
                        <div className={styles.formRadioGroup}>
                            <Radio
                                label={t('male_secondary')}
                                value="male"
                                {...register('gender')}
                            />
                            <Radio
                                {...register('gender')}
                                label={t('female_secondary')}
                                value="female" />
                        </div>
                    </FieldBlock>

                    <Controller
                        control={methods.control}
                        name={'dob'}
                        render={({ field: { onChange, value, ref } }) => {
                            return (
                                <DataPicker
                                    className={styles.datePicker}
                                    dob={methods.getValues('dob')}
                                    isRequireChanges={true}
                                    onChangeDate={(e) => {
                                        onChange(e)
                                        goalDobFieldChange();
                                    }} />
                            )
                        }} />

                    <FieldBlock>
                        <div className={styles.formLabel}>
                            <Typography
                                variant="label"
                                fontWeight="semibold"
                                className={styles.required}>
                                {t('travel_plans_label')}
                            </Typography>
                        </div>
                        <div
                            className={styles.travelPlans}
                            onClick={() => setPopupEdit(true)}>
                            {
                                userTravelPlans.length ?
                                    userTravelPlans
                                :
                                    <span className={styles.placeholder}>{t('travel_plans_placeholder')}</span>
                            }
                            <span className={styles.travelPlansLength}>{userTravelPlans.length} / 500</span>
                        </div>
                    </FieldBlock>
                </div>
                <ButtonSubmitStep
                    isLoading={isLoading}
                    stepNumber={1}
                    isDisabled={!isValid} />
            </form>
        </FormProvider>
    );
};
